"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { useId } from "react";
import { RegisterOptions, useFormContext, useFormState } from "react-hook-form";
import styles from "./InputField.module.scss";

export interface InputFieldProps {
	name: string;
	label: string;
	hideLabel?: boolean;
	className?: string;
	help?: string;
	placeholder?: string;
	registerOptions?: RegisterOptions;
	type?: "text" | "tel" | "email" | "url" | "textarea";
	inputProps?: React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
	hideError?: boolean;
	rows?: number; // For textarea only
}

export const InputField = ({
	name,
	label,
	hideLabel,
	className,
	help,
	placeholder,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
	type = "text",
	hideError,
	rows,
}: InputFieldProps) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { register } = useFormContext();
	const { errors } = useFormState();

	let InputElement: React.ElementType = "input";
	if (type === "textarea") {
		InputElement = "textarea";
	}

	return (
		<FieldWrapper
			name={name}
			label={label}
			hideLabel={hideLabel}
			required={registerOptions?.required as boolean}
			labelFor={id}
			className={className}
			errorId={errorId}
			hideError={hideError}
		>
			<div className={styles["input-wrapper"]}>
				<InputElement
					className={styles["input"]}
					placeholder={placeholder}
					id={id}
					rows={rows}
					type={type}
					aria-invalid={!!errors[name]}
					aria-required={!!registerOptions?.required}
					aria-describedby={
						[help ? helpId : "", errors[name] ? errorId : "", ariaDescribedBy]
							.filter(v => v)
							.join(" ") || undefined
					}
					{...register(name, registerOptions)}
					{...inputProps}
				/>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
